import {memo, useState} from 'react';
import styled from 'styled-components';
import {Palette} from 'hubbl-shared';
import {useAppSelector, useI18n} from '../../../../hooks';
import {EventSelector} from '../../../../store/events/EventSelector';
import {IconButton} from '../../../buttons/icon-button/IconButton';
import {AssetIconDiskette, AssetIconLoadMore} from '../../../../assets';

interface LoadPreviousEventsButtonProps {
  onLoadPrevious: () => Promise<void>;
}

export const LoadPreviousEventsButton = memo(
  ({onLoadPrevious}: LoadPreviousEventsButtonProps) => {
    const [isLoadingPrevious, setIsLoadingPrevious] = useState(false);
    const t = useI18n();

    const {hasMorePast} = useAppSelector(EventSelector.getEvents);
    const handleLoadPreviousEvents = async () => {
      if (!hasMorePast) return;

      setIsLoadingPrevious(true);
      try {
        await onLoadPrevious();
      } catch (error) {
        console.error('Error loading previous events:', error);
      } finally {
        setIsLoadingPrevious(false);
      }
    };

    if (!hasMorePast) return null;

    return (
      <div style={{display: 'flex', justifyContent: 'center'}}>
        <IconButton
          backgroundColor={Palette.whiteTransparent60}
          icon={<AssetIconLoadMore />}
          onClick={handleLoadPreviousEvents}
          disabled={isLoadingPrevious}
          text={
            isLoadingPrevious
              ? t.common.loading
              : t.scenes.pastEvents.loadPreviousEvents
          }
          data-timeline-item
        />
      </div>
    );
  },
);

const LoadPreviousButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 1rem 0;
  padding: 0.75rem 1.5rem;
  background: ${Palette.white};
  border: 2px solid ${Palette.almostBlack250};
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  font-size: 0.875rem;
  font-weight: 600;
  color: ${Palette.almostBlack250};
  min-height: 60px;
  width: 100%;
  max-width: 300px;
  align-self: center;

  &:hover:not(:disabled) {
    background: ${Palette.almostBlack250};
    color: ${Palette.white};
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const LoadPreviousContent = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 1rem;
`;
